import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getReels } from '../api/rttsClient';
import type { ReelSummary } from '../api/types';
import {
  REELS_BADGE_ACTIVE_POLL_MS,
  REELS_BADGE_BACKOFF_BASE_MS,
  REELS_BADGE_BACKOFF_MAX_MS,
  REELS_BADGE_IDLE_POLL_MS,
  REELS_BADGE_MAX_FAILURES,
  isStudioJobInProgress,
} from '../config';
import { useStudioAuth } from './useStudioAuth';

type StudioReelsBadgeValue = {
  inProgressCount: number;
  reportReels: (reels: ReelSummary[]) => void;
};

const StudioReelsBadgeContext = createContext<StudioReelsBadgeValue>({
  inProgressCount: 0,
  reportReels: () => undefined,
});

function countInProgress(reels: ReelSummary[]): number {
  return reels.filter((r) => isStudioJobInProgress(r.status)).length;
}

function nextBackoffMs(failures: number): number {
  const exp = Math.max(0, failures - 1);
  return Math.min(
    REELS_BADGE_BACKOFF_MAX_MS,
    REELS_BADGE_BACKOFF_BASE_MS * 2 ** exp
  );
}

/**
 * Cheap app-wide in-progress count for the My Reels nav badge.
 * Backs off / halts on repeated errors so a broken list endpoint cannot
 * tight-loop 500; resumes normal cadence after a successful poll.
 * Polls on linked-session lifetime — not on every pathname change.
 */
export const StudioReelsBadgeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { status, isLoggedOut } = useStudioAuth();
  const [inProgressCount, setInProgressCount] = useState(0);
  const countRef = useRef(0);
  const failuresRef = useRef(0);
  const haltedRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reportReels = useCallback((reels: ReelSummary[]) => {
    const next = countInProgress(reels);
    countRef.current = next;
    setInProgressCount(next);
  }, []);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const schedule = useCallback(
    (delayMs: number, tick: () => void) => {
      clearTimer();
      timeoutRef.current = setTimeout(tick, delayMs);
    },
    [clearTimer]
  );

  useEffect(() => {
    if (isLoggedOut || status !== 'linked') {
      countRef.current = 0;
      setInProgressCount(0);
      failuresRef.current = 0;
      haltedRef.current = false;
      clearTimer();
      return;
    }

    failuresRef.current = 0;
    haltedRef.current = false;

    let cancelled = false;

    const tick = async () => {
      if (cancelled || haltedRef.current) return;
      try {
        const { reels } = await getReels();
        if (cancelled) return;
        failuresRef.current = 0;
        reportReels(reels);
        const nextMs =
          countRef.current > 0
            ? REELS_BADGE_ACTIVE_POLL_MS
            : REELS_BADGE_IDLE_POLL_MS;
        schedule(nextMs, () => {
          void tick();
        });
      } catch {
        if (cancelled) return;
        // Fail silently — badge simply stays empty / last known.
        failuresRef.current += 1;
        if (failuresRef.current >= REELS_BADGE_MAX_FAILURES) {
          haltedRef.current = true;
          clearTimer();
          return;
        }
        schedule(nextBackoffMs(failuresRef.current), () => {
          void tick();
        });
      }
    };

    void tick();

    return () => {
      cancelled = true;
      clearTimer();
    };
  }, [isLoggedOut, status, reportReels, schedule, clearTimer]);

  const value = useMemo(
    () => ({ inProgressCount, reportReels }),
    [inProgressCount, reportReels]
  );

  return (
    <StudioReelsBadgeContext.Provider value={value}>
      {children}
    </StudioReelsBadgeContext.Provider>
  );
};

export function useStudioReelsBadge(): StudioReelsBadgeValue {
  return useContext(StudioReelsBadgeContext);
}

/** Test helper — exported backoff math for unit coverage. */
export function __testOnlyNextBackoffMs(failures: number): number {
  return nextBackoffMs(failures);
}
